import { matchPath } from 'react-router-dom';

export interface PortalUpDestination {
  to: string;
  label: string;
}

function matchParams(pattern: string, pathname: string): Record<string, string | undefined> | null {
  return matchPath({ path: pattern, end: true }, pathname)?.params ?? null;
}

/**
 * Stable information-hierarchy parents for every protected Parent Portal route.
 * This intentionally does not use browser history: an authenticated deep link
 * must never send a parent out of the Portal when they choose Up.
 */
export function getPortalUpDestination(pathname: string, search = ''): PortalUpDestination | null {
  if (
    pathname === '/portal' ||
    pathname === '/portal/' ||
    pathname === '/portal/explore' ||
    pathname === '/portal/approvals' ||
    pathname === '/portal/more'
  ) {
    return null;
  }

  const course = matchParams('/portal/courses/:courseSlug', pathname);
  if (course) {
    return { to: '/portal/courses#catalogue', label: 'Courses' };
  }

  const teacher = matchParams('/portal/tutoring/:slug', pathname);
  if (teacher) {
    const from = new URLSearchParams(search).get('from');
    return {
      to: from ? `/portal/tutoring?${from}` : '/portal/tutoring',
      label: 'Tutoring',
    };
  }

  const academyCheckout = matchParams('/portal/academy/checkout/:sku', pathname);
  if (academyCheckout?.sku) {
    return {
      to: `/portal/academy/products/${encodeURIComponent(academyCheckout.sku)}`,
      label: 'Exam details',
    };
  }
  if (
    matchParams('/portal/academy/products/:slug', pathname) ||
    matchParams('/portal/academy/orders/:intentId', pathname) ||
    matchParams('/portal/academy/reports/:sessionId', pathname)
  ) {
    return { to: '/portal/academy', label: 'Exam Prep' };
  }

  if (matchParams('/portal/checkout/class/:classId', pathname)) {
    return { to: '/portal/courses#available-times', label: 'Courses' };
  }

  const challenge =
    matchParams('/portal/challenge/:slug/register', pathname) ??
    matchParams('/portal/challenge/:slug/report', pathname);
  if (challenge?.slug) {
    return {
      to: `/portal/challenge/${encodeURIComponent(challenge.slug)}`,
      label: 'Code challenge',
    };
  }

  if (matchParams('/portal/challenge/:slug', pathname)) {
    return { to: '/portal/explore', label: 'Explore' };
  }

  if (pathname === '/portal/family/new') return { to: '/portal', label: 'Home' };

  const kidChild =
    matchParams('/portal/family/:kidId/projects', pathname) ??
    matchParams('/portal/family/:kidId/passport', pathname) ??
    matchParams('/portal/family/:kidId/images', pathname) ??
    matchParams('/portal/family/:kidId/study', pathname) ??
    matchParams('/portal/family/:kidId/settings', pathname);
  if (kidChild?.kidId) {
    return {
      to: `/portal/family/${encodeURIComponent(kidChild.kidId)}`,
      label: 'Activities',
    };
  }
  if (matchParams('/portal/family/:kidId', pathname)) {
    return { to: '/portal', label: 'Home' };
  }

  if (matchParams('/portal/audit/project/:id', pathname)) {
    return { to: '/portal/approvals', label: 'Notifications' };
  }
  if (
    pathname === '/portal/wallet/topup' ||
    pathname === '/portal/wallet/auto-topup' ||
    pathname === '/portal/wallet/stars-guide'
  ) {
    return { to: '/portal/wallet', label: 'Wallet and limits' };
  }
  if (matchParams('/portal/usage/:kidId', pathname)) {
    return { to: '/portal/usage', label: 'Star usage' };
  }

  const exploreOwnedTopLevel = new Set([
    '/portal/courses',
    '/portal/classes',
    '/portal/academy',
    '/portal/tutoring',
    '/portal/guides',
    '/portal/gallery',
  ]);
  if (exploreOwnedTopLevel.has(pathname)) {
    return { to: '/portal/explore', label: 'Explore' };
  }

  const moreOwnedTopLevel = new Set([
    '/portal/projects',
    '/portal/academy/hsc-planner',
    '/portal/billing',
    '/portal/settings',
  ]);
  if (moreOwnedTopLevel.has(pathname)) {
    return { to: '/portal/more', label: 'More' };
  }

  // Stars remains a header menu rather than a route. Its top-level destinations
  // move Up to the stable Portal root.
  return { to: '/portal', label: 'Home' };
}
